/**
 * CopyDescribeStep — Step 1 of CopyCampaignWorkflow ("What are you doing?").
 *
 * Ported from mock-start.html. Class names come straight from
 * copy-workflow.css's class inventory (see that file's header comment for
 * the cw- prefix / disambiguation rules) — nothing here is invented.
 *
 * Behaviourally: a free-text prompt is sent to interpretCopyInstruction
 * (stubbed — see copyWorkflowApi.js) which returns an op list the user can
 * toggle off (declining an op is the recovery path for a misread, per the
 * mock's own footer copy). The 4 REQUIRED fields (start/end/budget/budget
 * type) are NOT part of that op list's `ops[]` — they come from
 * `fields`/`unresolved` in the interpreter's return shape and are always
 * editable here directly, LLM or not; the "manual parameters" escape hatch
 * just exposes the same formFields state without ever calling the LLM.
 *
 * GAP #1 — per-op inline editing (see this task's brief): ops were
 * previously toggle-only (accept/decline the LLM's guess as stated in its
 * `label` string, never correct the guess itself). Two ops now also carry an
 * inline, directly-editable value, ADDITIVE to the existing checkbox:
 *   - `replace`/`market` (mock-start.html: both driven by one shared
 *     `F.target`, e.g. "Replace Saudi -> [Kuwait]") — a single shared target
 *     string, editable inline within the op's own label text.
 *   - `rename` (mock-start.html: `inp('n0',...)..inp('n3',...)`, one input
 *     per source campaign, prefilled from a guessed new name) — one editable
 *     name per `sourceCampaigns` entry. SUPERSEDED: the Campaign name
 *     FieldRow below is now the flow's real name input, always visible rather
 *     than living inside this flag-hidden op list, and both surfaces read and
 *     write the same value through nameFor/handleNameChange.
 * copyWorkflowApi.js is OUT OF SCOPE for this task, so neither value is read
 * from a dedicated field on the interpretation stub (the real
 * copy_intent_reasoning.py's documented op shape is exactly
 * `{id, kind, label, hits, applied, consequence, blocks}` — no `value`/
 * `target` field exists there either). Instead:
 *   - the shared target guess is parsed back out of the CURRENT stub's
 *     `label` text for the `replace`/`market` ops (see
 *     TARGET_LABEL_PATTERNS/splitTargetLabel below) — if the stub's label
 *     wording ever changes, this simply stops matching and those two ops
 *     fall back to the old plain, non-editable rendering (never a crash,
 *     never a stale/wrong value silently shown as "live").
 *   - the per-campaign rename guess is NOT parsed from anything (the stub's
 *     `rename` op label only ever names the PRIMARY campaign, singular, so
 *     there is no per-campaign guess to parse for campaigns 2..N) — each
 *     campaign instead shows the pre-filled default the workflow froze when
 *     the source loaded (`nameDefaults`, from defaultCopyCampaignName:
 *     `<source name> <date> <time>`). Nothing is fabricated: that default is
 *     the same value the visible Campaign name field starts from.
 * TODO(backend): once copy_intent_reasoning.py's return shape is extended
 * (in-scope, dedicated task) to carry a structured target string and/or
 * per-campaign name guesses, prefer reading those directly over the label
 * parsing here. A per-campaign name guess would seed `nameDefaults`; it must
 * NOT be written into renameOverrides, which means "the user chose this".
 */
import { useCallback, useState } from 'react';
import {
  normalizeCopyPlatform, PLATFORM_CHIP_CLASS, PLATFORM_CHIP_LABEL,
  SHOW_COPY_INTERPRET_UI, COPY_STEP1_LABEL, COPY_STEP1_LEDE,
  COPY_STEP1_CARD_TITLE, COPY_STEP1_CARD_SUB, copyStepLabels,
  copyBudgetIsImpressions, isGoogleAdsCopyPlatform, CAMPAIGN_NAME_MAX,
  fetchGoogleLocationSuggestions } from './copyWorkflowApi';
import { getPlatformName } from '../../utils/platformNames';
import CopyStepper from './CopyStepper';
import CopyGeoSuggest from './CopyGeoSuggest';

const HINTS = [
  '…as a Diwali burst, 3 weeks, double the budget',
  '…as a base for a new dealer',
  '…but run it as PMax',
];

const INTENT_OPTIONS = [
  { value: 'new_market', label: 'New market' },
  { value: 'new_push', label: 'New push' },
  { value: 'new_advertiser', label: 'New advertiser' },
  { value: 'new_tactic', label: 'New tactic' },
];

// Per-intent guidance shown under the intent selector — mirrors the 4
// behavioral rules copy_intent_reasoning.py enforces deterministically for
// each intent (see that module's "Per-intent rules" doc section).
const INTENT_NOTES = {
  new_market: 'Any lingering reference to the source market is treated as a hard problem — it will always show up as a blocking op, never silently dropped.',
  new_push: 'Geo/language stay as-is on purpose. A non-blocking cannibalization check runs instead (same market, overlapping keywords/flight).',
  new_advertiser: 'The strictest intent — name, URLs, brand keywords, logo/image assets and conversion actions are all forced to require replacement, even if the LLM thought otherwise.',
  new_tactic: 'Keyword-only settings (match types, negatives, per-keyword bids) have no equivalent in the target tactic and are always surfaced as dropped, never silently migrated.',
};

const OP_ICONS = { replace: 'find_replace', market: 'public', rename: 'badge' };

// Ops whose label embeds ONE inferred target-market/value string that a user
// should be able to correct inline (Gap #1, item 1 — see the module doc
// comment above for why this parses `label` rather than reading a field).
// Each pattern must capture exactly the surrounding text and the guessed
// value so the input can be spliced back into the same position the LLM put
// it, without disturbing the rest of the sentence.
const TARGET_LABEL_PATTERNS = {
  replace: /^(Replace source references → )(.+)( in names, headlines, descriptions and final URLs)$/,
  market: /^(Change market — geo\/language\/conversion action move to )(.+)$/,
};

function splitTargetLabel(op) {
  const pattern = TARGET_LABEL_PATTERNS[op.id];
  if (!pattern) return null;
  const m = String(op.label || '').match(pattern);
  if (!m) return null;
  return { before: m[1], target: m[2], after: m[3] || '' };
}

const RENAME_OP_ID = 'rename';

function ScopeRow({ icon, children, hits }) {
  return (
    <div className="op scope">
      <span className="ic scope"><span className="material-icons">{icon}</span></span>
      <span className="tx">{children}</span>
      {hits && <span className="hits">{hits}</span>}
    </div>
  );
}

function FieldRow({ icon, label, need, children }) {
  return (
    <div className={`op field${need ? ' need' : ''}`}>
      <span className="ic fieldic"><span className="material-icons">{icon}</span></span>
      <span className="tx">{label}</span>
      {children}
    </div>
  );
}

// `targetEditor` (replace/market — Gap #1 item 1) and `renameEditor` (rename
// — Gap #1 item 2) are both optional and purely ADDITIVE to the existing
// accept/decline checkbox below; neither replaces `onToggle`/`applied`.
function ToggleOpRow({
  op, applied, onToggle, targetEditor, renameEditor,
}) {
  const icon = OP_ICONS[op.id] || (op.kind === 'flag' ? 'info' : 'tune');
  // Flags are advisory ("the system can't do this for you") and render locked —
  // EXCEPT when the interpreter attached structured field_changes, i.e. it CAN
  // express the fix as a concrete text edit (copy_op_applier.py applies it at
  // build time). Those flags are accept/declinable like any toggle.
  const checkable = op.kind !== 'flag'
    || (Array.isArray(op.field_changes) && op.field_changes.length > 0);
  return (
    <div className={`op${!applied ? ' off' : ''}`}>
      {checkable ? (
        <span className={`cb${applied ? ' on' : ''}`} onClick={() => onToggle(op.id, !applied)} />
      ) : (
        <span className="cb lock" />
      )}
      <span className="ic"><span className="material-icons">{icon}</span></span>
      <div className="tx">
        {targetEditor ? (
          <>
            {targetEditor.before}
            <input
              className="inline-in"
              value={targetEditor.value}
              onChange={(e) => targetEditor.onChange(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
            {targetEditor.after}
          </>
        ) : op.label}
        {renameEditor && (
          <div className="namelist">
            {renameEditor.campaigns.map((c) => (
              <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span className="fieldnote" style={{ marginLeft: 0 }}>{c.name}</span>
                <span className="material-icons arrow">east</span>
                <input
                  className="inline-in"
                  value={renameEditor.valueFor(c)}
                  onChange={(e) => renameEditor.onChange(c.id, e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            ))}
          </div>
        )}
      </div>
      <span className="hits">{op.hits} hits</span>
      {!applied && op.consequence && (
        <div className={`conseq${op.blocks ? ' blocks' : ''}`}>{op.consequence}{op.blocks ? ' Blocks the push.' : ''}</div>
      )}
    </div>
  );
}

export default function CopyDescribeStep({
  // Step rail labels — always the flow's 3 entries; defaulted so a caller that
  // doesn't pass them still renders the right rail.
  stepLabels = copyStepLabels(),
  sourceCampaigns, sourcesLoading, sourcesError, platform,
  prompt, setPrompt,
  interpretation, interpreting, onReRead,
  intent, setIntent,
  opOverrides, setOpOverrides,
  renameOverrides, setRenameOverrides,
  // id -> the pre-filled `<source name> <date> <time>` name, frozen when the source
  // loaded (CopyCampaignWorkflow). Shown as the field's value until the user types, at
  // which point renameOverrides takes over — see nameFor/handleNameChange below.
  nameDefaults = {},
  // Set when the pre-build uniqueness check found the chosen name already in use in
  // this Google Ads account (CopyCampaignWorkflow's goToAdjust). Shown on the Campaign
  // name row and in the error banner, and cleared as soon as the name is edited.
  nameError = null,
  nameChecking = false,
  onNameEdited = () => {},
  formFields, setFormFields,
  dv360Geo, setDv360Geo,
  searchGeo, setSearchGeo,
  // The account and user the copy is made by: Google location suggestions are looked up
  // through the source campaign's Google connection, which must belong to the account,
  // and the campaigns routes require the user.
  accountId = null,
  userId = null,
  targetAdvertiserId, setTargetAdvertiserId,
  // GOOGLE ADS destination account. Defaulted so the DV360/TTD render paths and the
  // older tests still mount without passing them. copyToSameAccount defaults TRUE —
  // the same-account copy is the common case and the behaviour that predates this
  // field, so an untouched step 1 sends nothing new.
  copyToSameAccount = true, setCopyToSameAccount = () => {},
  targetCustomerId = '', setTargetCustomerId = () => {},
  // Google Ads (Search + PMax) overrides — { targetCpa } only, since all URL
  // tracking moved to step 2's URL panel. Defaulted so a caller that doesn't pass
  // them (the DV360/TTD render paths, and older tests) still mounts; the field
  // reads through `gadsOverride`, never the object directly.
  gadsOverrides = {}, setGadsOverrides = () => {},
  manualOpen, setManualOpen,
  onBack, onContinue,
}) {
  const [localError, setLocalError] = useState(null);
  // Shared target-market value editable inline on the replace/market ops
  // (Gap #1 item 1). Kept local rather than lifted to CopyCampaignWorkflow —
  // neither CopyPlanStep's row builders nor CopyPushStep's push payload
  // reference a target-market string today (checked both in full), so there
  // is no existing consumer to thread this into yet. `null` means "no edit
  // yet, show the LLM's own guess" (parsed via splitTargetLabel below) —
  // mirrors how `opOverrides` already treats "absent key" as "use op.applied".
  const [targetOverride, setTargetOverride] = useState(null);

  const ops = interpretation?.ops || [];
  const effectiveApplied = (op) => (Object.prototype.hasOwnProperty.call(opOverrides, op.id) ? opOverrides[op.id] : op.applied);
  const toggleCount = ops.filter((op) => op.kind !== 'flag').length;
  const appliedCount = ops.filter((op) => op.kind !== 'flag' && effectiveApplied(op)).length;
  const declinedBlocking = ops.filter((op) => op.kind !== 'flag' && !effectiveApplied(op) && op.blocks).length;

  // END DATE is optional for Google Ads copies: a Search/PMax campaign may run always-on
  // (search_campaign_validator: "END DATE IS OPTIONAL"; the PMax validator enforces no
  // date rules at all). It stays required for DV360/TTD/Facebook, whose copiers build
  // insertion orders / ad groups / ad sets that need a bounded flight.
  const endDateRequired = !isGoogleAdsCopyPlatform(platform);
  // No end date = always-on = DAILY pacing only. Two things follow, and they are the
  // whole feature: a total budget has no flight to spread across (so Daily is the only
  // type offered), and the source-budget carry-forward has nothing to pace against (so
  // the daily number stops being optional). The backend and both copy launchers enforce
  // the identical rule -- this only decides what the form lets you submit.
  // Gated on endDateRequired, not on the blank date alone: for DV360/TTD an empty end
  // date is an UNFINISHED form, not an always-on campaign, so their budget-type choice
  // must not flicker while the user is still filling the flight in.
  const dailyOnly = !endDateRequired && !formFields.endDate;

  // Budget is optional for Google Ads copies (empty carries the source budget forward);
  // still required for DV360/TTD, whose copiers can't derive it from the source -- and
  // for an always-on copy, per dailyOnly above.
  const budgetRequired = platform === 'dv360' || platform === 'ttd' || dailyOnly;

  // The number typed here is an IMPRESSION COUNT for DV360 and currency everywhere else
  // (copy_dv360_campaign_launcher builds every insertion order BUDGET_UNIT_IMPRESSIONS and
  // reads `budget` as the impression total). Same source of truth the Review-and-launch
  // summary formats from, so the two screens can't disagree about what was entered.
  const budgetInImpressions = copyBudgetIsImpressions(platform);
  const budgetUnitWord = budgetInImpressions ? 'impressions' : 'amount';
  const budgetLabel = budgetInImpressions
    ? 'Budget (impressions)'
    : (dailyOnly ? 'Daily budget' : 'Budget');
  const budgetNote = (() => {
    if (budgetInImpressions) return 'Enter a number of impressions, not dollars.';
    if (dailyOnly) return 'Required — there is no end date to pace a total budget against.';
    return 'Leave blank to keep the source budget.';
  })();

  // The three Google Ads override rows (target CPA + the two UTM levels) render for
  // Search and PMax only — DV360/TTD's copy launchers have no such inputs, and the
  // backend re-gates on the same two platforms.
  const isGoogleAds = platform === 'google_search' || platform === 'google_pmax';
  // Suggestions go through the source campaign's own Google connection.
  const geoChannelId = (sourceCampaigns || []).find((c) => c?.channel_id)?.channel_id || null;
  const suggestGeo = useCallback(
    (name, signal) => fetchGoogleLocationSuggestions({ accountId, userId, channelId: geoChannelId, name, signal }),
    [accountId, userId, geoChannelId]);
  // `''` is the real empty value for every one of these (the state seeds them blank);
  // the ?? keeps a caller that passed a partial object from rendering `undefined` into
  // an input and flipping it to uncontrolled.
  const gadsOverride = (key) => gadsOverrides?.[key] ?? '';
  const setGadsOverride = (key, value) => setGadsOverrides((prev) => ({ ...prev, [key]: value }));
  // The campaign name is USER INPUT with a pre-filled default, so it reads from
  // renameOverrides first and falls back to the frozen default. `''` is a real value here
  // (the user cleared the box) and must not fall back, hence hasOwnProperty rather than
  // a `||` chain.
  const nameFor = (c) => (
    Object.prototype.hasOwnProperty.call(renameOverrides, c.id)
      ? renameOverrides[c.id]
      : (nameDefaults[c.id] ?? '')
  );
  const handleNameChange = (campaignId, value) => {
    onNameEdited();
    setRenameOverrides((prev) => ({ ...prev, [campaignId]: value }));
  };
  // A campaigns row can be a SHELL standing for several platform campaigns (a
  // comma-joined platform_campaign_id — real, not hypothetical: see
  // campaign_copy_handler._is_merged_campaign_id). The backend then names each copy
  // "<this name> - <that campaign's source name>", so the launched names will not match
  // this box verbatim. Say so here rather than letting it look like a bug later.
  const shellCampaignCount = (c) => {
    const ids = String(c?.platform_campaign_id || '').split(',').map((x) => x.trim()).filter(Boolean);
    return ids.length > 1 ? ids.length : 0;
  };
  // Required, like the flight: the field is pre-filled, so this only bites when someone
  // clears it — at which point the copy would fall back to a name they didn't choose.
  const namesMissing = sourceCampaigns.some((c) => !String(nameFor(c) || '').trim());

  // Clearing the end date forces the type back to daily IN THE SAME HANDLER rather than
  // in an effect: a user who picks Total and then clears the date would otherwise leave
  // 'total' in the payload with no end date to spread it over — the exact combination the
  // route rejects. Doing it here means that state is never observable.
  const handleEndDateChange = (value) => setFormFields((prev) => ({
    ...prev,
    endDate: value,
    ...(value ? {} : { budgetType: 'daily' }),
  }));

  const requiredFields = [
    formFields.startDate,
    ...(endDateRequired ? [formFields.endDate] : []),
    ...(budgetRequired ? [formFields.budget] : []),
  ];
  // The name rides the same count so one `unresolvedCount === 0` answers for every
  // required input — the chip that reports "N still needed" must not disagree with the
  // button that refuses to continue.
  const unresolvedCount = requiredFields.filter((v) => v === '' || v == null).length
    + (namesMissing ? 1 : 0);

  const canContinue = unresolvedCount === 0;

  const handleHint = (hint) => {
    setPrompt((prev) => (prev ? `${prev.replace(/\s+$/, '')} ${hint.replace(/^…/, '')}` : hint.replace(/^…/, '')));
  };

  const handleContinue = () => {
    if (!canContinue) {
      const budgetWord = budgetInImpressions ? 'impressions' : 'a budget';
      let message;
      // The name comes FIRST and gets its own sentence: it is the one required field that
      // starts FILLED, so any flight/budget wording would send the user hunting the wrong
      // box for a value that is sitting there already.
      if (namesMissing) {
        message = 'Give the copy a campaign name to continue.';
      } else if (dailyOnly) {
        message = 'Add a start date and a daily budget to continue — with no end date the '
          + 'copy runs always-on and paces on its daily budget.';
      } else if (budgetRequired) {
        message = `Add a start date, an end date and ${budgetWord} to continue.`;
      } else {
        message = 'Add a start date and an end date to continue.';
      }
      setLocalError(message);
      return;
    }
    setLocalError(null);
    onContinue();
  };

  return (
    <div className="cw-pane">
      <div className="cw-scroll">
        <div className="wrap">
          <CopyStepper steps={stepLabels} current={0} />
          <div>
            <h1 className="cw-h1-lg">{COPY_STEP1_LABEL}</h1>
            <p className="lede">{COPY_STEP1_LEDE}</p>
          </div>

          {sourcesError && (
            <div className="gate-banner">
              <span className="material-icons">error</span>
              <div className="t">Couldn&apos;t load the campaign to copy: {sourcesError}</div>
            </div>
          )}

          {SHOW_COPY_INTERPRET_UI && (
          <div className="prompt">
            <div className="prompt-top"><span className="material-icons">auto_awesome</span> Describe the copy</div>
            <textarea
              rows={2}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="e.g. copy this as the foundation for Kuwait, same structure, start mid-August"
            />
            <div className="prompt-foot">
              <span className="pillnote">Try:</span>
              {HINTS.map((h) => (
                <span key={h} className="hintchip" onClick={() => handleHint(h)}>{h}</span>
              ))}
              <div className="gap" />
              <button
                className="cw-btn cw-btn-primary cw-btn-sm"
                onClick={onReRead}
                disabled={interpreting || !prompt.trim()}
              >
                <span className="material-icons">bolt</span> {interpreting ? 'Interpreting…' : 'Interpret'}
              </button>
            </div>
          </div>
          )}

          <div className="cw-card cw-card-describe">
            <div className="cw-card-h">
              <span className="material-icons" style={{ color: '#00b98c' }}>auto_awesome</span>
              <span className="ttl">{COPY_STEP1_CARD_TITLE}</span>
              <span className="sub">{COPY_STEP1_CARD_SUB}</span>
              <div className="gap" />
              <span className="sub">{unresolvedCount} need{unresolvedCount === 1 ? 's' : ''} a value</span>
            </div>
            <div className="cw-card-b" style={{ gap: 0 }}>
              <ScopeRow icon="inventory_2" hits={`${sourceCampaigns.length} campaign${sourceCampaigns.length === 1 ? '' : 's'}`}>
                {sourcesLoading ? 'Loading source campaigns…' : (
                  <div className="srclist">
                    {sourceCampaigns.map((c) => {
                      // c.destination is a raw DB slug (e.g. "google-ads-pmax",
                      // "google_search") — never render it directly. Normalize
                      // first so the 3 platforms this workflow supports get the
                      // same short chip label used everywhere else in it
                      // (Search/DV360/TTD); anything outside that (e.g. a PMax
                      // campaign someone picked via the entry browser, which
                      // doesn't filter by platform) falls back to the fuller
                      // formatted name rather than an unformatted raw slug.
                      const normalized = normalizeCopyPlatform(c.destination) || platform;
                      const chipLabel = PLATFORM_CHIP_LABEL[normalized] || getPlatformName(c.destination);
                      return (
                        <div className="src" key={c.id}>
                          <span className={`ch ${PLATFORM_CHIP_CLASS[normalized] || 'ch-srch'}`}>
                            {chipLabel}
                          </span>
                          <span>{c.name}</span>
                          <span className="b">{c.id}</span>
                        </div>
                      );
                    })}
                    {sourceCampaigns.length === 0 && <span className="muted">No source campaigns selected yet.</span>}
                  </div>
                )}
              </ScopeRow>
              {SHOW_COPY_INTERPRET_UI && (
              <ScopeRow icon="alt_route" hits="sets the guardrails">
                <select className="inline-sel" value={intent} onChange={(e) => setIntent(e.target.value)}>
                  {INTENT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <div className="scopenote">{INTENT_NOTES[intent]}</div>
              </ScopeRow>
              )}

              {SHOW_COPY_INTERPRET_UI && ops.map((op) => {
                const targetSplit = splitTargetLabel(op);
                const targetEditor = targetSplit ? {
                  before: targetSplit.before,
                  after: targetSplit.after,
                  value: targetOverride != null ? targetOverride : targetSplit.target,
                  onChange: setTargetOverride,
                } : null;
                const renameEditor = op.id === RENAME_OP_ID ? {
                  campaigns: sourceCampaigns,
                  // Same value and same writer as the Campaign name field above — this
                  // surface used to default to "Copy of <name>", which would now
                  // contradict the pre-filled name if the interpret UI were re-enabled.
                  valueFor: nameFor,
                  onChange: handleNameChange,
                } : null;
                return (
                  <ToggleOpRow
                    key={op.id}
                    op={op}
                    applied={effectiveApplied(op)}
                    onToggle={(id, next) => setOpOverrides((prev) => ({ ...prev, [id]: next }))}
                    targetEditor={targetEditor}
                    renameEditor={renameEditor}
                  />
                );
              })}
              {SHOW_COPY_INTERPRET_UI && !interpretation && (
                <div className="op"><span className="tx muted">Describe the copy above and hit Interpret to generate the operation list — or set the parameters manually below.</span></div>
              )}

              {/* CAMPAIGN NAME — the first thing the copy needs and the first field
                  shown, because it is the one the user is most likely to change. Before
                  this existed the name was ASSUMED: buildPayloadFor sent
                  `<source name> (copy)`, the same string every time, so a second copy of
                  one campaign collided with the first (and Google Ads rejects duplicate
                  campaign names in an account). One row per source campaign, which is one
                  row in practice — the flow copies a single campaign per run. */}
              {sourceCampaigns.map((c, i) => {
                const shellCount = shellCampaignCount(c);
                return (
                  <FieldRow
                    key={c.id ?? i}
                    icon="drive_file_rename_outline"
                    label={sourceCampaigns.length > 1 ? `Campaign name (${c.name})` : 'Campaign name'}
                    need={!String(nameFor(c) || '').trim() || Boolean(nameError)}
                  >
                    <input
                      type="text"
                      className="need-in cw-name-in"
                      value={nameFor(c)}
                      maxLength={CAMPAIGN_NAME_MAX}
                      placeholder="Name for the new campaign"
                      onChange={(e) => handleNameChange(c.id, e.target.value)}
                    />
                    <span className="fieldnote">
                      {nameError
                        ? 'Already used in this account — enter a different name.'
                        : (shellCount
                          ? `This row covers ${shellCount} campaigns — each copy is named "<this> - <its source campaign name>".`
                          : 'Pre-filled from the source campaign and the time you started this copy.')}
                      {/* The name is not only a label. Where the source's landing URLs tag
                          with its own campaign name (…?utm_campaign=<source name>, which is
                          how these accounts tag), the copy rewrites that value to THIS name
                          — otherwise every click on the copy reports as the source. Said
                          here because the field is pre-filled with a timestamp: left alone,
                          that timestamp ends up in the analytics, and the review step is
                          otherwise the first place anyone would find out. */}
                      {isGoogleAds && !nameError && (
                        <> Also used as the <code>utm_campaign</code> value wherever the
                          source&apos;s URLs tag with its campaign name — change it, or set{' '}
                          <code>utm_campaign</code> in the URL panel on the next step.
                        </>
                      )}
                    </span>
                  </FieldRow>
                );
              })}
              <FieldRow icon="event" label="Start date" need={!formFields.startDate}>
                <input
                  type="date"
                  className="need-in"
                  value={formFields.startDate}
                  onChange={(e) => setFormFields((prev) => ({ ...prev, startDate: e.target.value }))}
                />
              </FieldRow>
              <FieldRow
                icon="event_busy"
                label="End date"
                need={endDateRequired && !formFields.endDate}
              >
                <input
                  type="date"
                  className="need-in"
                  value={formFields.endDate}
                  min={formFields.startDate || undefined}
                  onChange={(e) => handleEndDateChange(e.target.value)}
                />
                {!endDateRequired && (
                  <span className="fieldnote">
                    Leave blank for an always-on campaign — needs a daily budget.
                  </span>
                )}
              </FieldRow>
              <FieldRow
                icon={budgetInImpressions ? 'visibility' : 'payments'}
                label={budgetLabel}
                need={budgetRequired && (formFields.budget === '' || formFields.budget == null)}
              >
                <input
                  type="number"
                  min="0"
                  // Cents are meaningless for an impression count, and a fractional
                  // impression is not a thing DV360 can be asked for.
                  step={budgetInImpressions ? '1' : '0.01'}
                  className="need-in"
                  placeholder={budgetRequired
                    ? budgetUnitWord
                    : `${budgetUnitWord} — leave blank to keep the source budget`}
                  value={formFields.budget}
                  onChange={(e) => setFormFields((prev) => ({ ...prev, budget: e.target.value }))}
                />
                <select
                  className="inline-sel"
                  value={formFields.budgetType}
                  onChange={(e) => setFormFields((prev) => ({ ...prev, budgetType: e.target.value }))}
                >
                  <option value="daily">Daily</option>
                  {/* Total is OFFERED only with an end date to spread it across. Removing
                      the option (rather than disabling it) is what keeps the form from
                      being able to submit a combination the backend refuses. */}
                  {!dailyOnly && <option value="total">Total</option>}
                </select>
                <span className="fieldnote">{budgetNote}</span>
              </FieldRow>
              {/* DV360-ONLY geo override. Rendered only for platform === 'dv360' so it never
                  appears for TTD / Google Ads Search / PMax, which share this step but whose
                  copy launchers have no geo-override input. Campaign-wide: one location name per
                  line; blank keeps the source geo. The backend re-gates on dv360 as well. */}
              {platform === 'dv360' && (
                <FieldRow icon="place" label="Geos">
                  <div className="cw-geo-override">
                    <textarea
                      className="need-in cw-geo-ta"
                      rows={2}
                      placeholder="Include — one location per line, e.g. Chicago, Illinois, United States"
                      value={dv360Geo?.included || ''}
                      onChange={(e) => setDv360Geo((prev) => ({ ...prev, included: e.target.value }))}
                    />
                    <textarea
                      className="need-in cw-geo-ta"
                      rows={2}
                      placeholder="Exclude (optional) — one location per line"
                      value={dv360Geo?.excluded || ''}
                      onChange={(e) => setDv360Geo((prev) => ({ ...prev, excluded: e.target.value }))}
                    />
                  </div>
                  <span className="fieldnote">Replaces the source&apos;s locations on every line item. Leave blank to keep them.</span>
                </FieldRow>
              )}
              {/* DV360 TARGET ADVERTISER. Create the copy under a different advertiser than the
                  source. Blank keeps the source advertiser (ordinary same-advertiser copy). A
                  different advertiser makes a cross-advertiser copy: advertiser-scoped creatives
                  and audiences can't carry over and are dropped. Backend re-gates on dv360. */}
              {platform === 'dv360' && (
                <FieldRow icon="business" label="Target advertiser">
                  <input
                    type="text"
                    inputMode="numeric"
                    className="need-in"
                    placeholder="Advertiser ID — leave blank to keep the source advertiser"
                    value={targetAdvertiserId || ''}
                    onChange={(e) => setTargetAdvertiserId(e.target.value)}
                  />
                  <span className="fieldnote">Copies into a different advertiser. Its creatives and audiences won&apos;t come across.</span>
                </FieldRow>
              )}
              {/* GOOGLE ADS DESTINATION ACCOUNT. Which Google Ads account the copy is
                  CREATED in. Labelled "advertiser" because that is the word the people
                  running these copies use; in the API it is a customer id.

                  The CHECKBOX is what decides, not the text box being blank. Deciding on
                  "is the box empty" would mean clearing the field silently re-points the
                  copy at the source account — a destructive default hidden behind an
                  innocuous edit. Checked (the default) keeps the copy in the source
                  account, exactly as every copy behaved before this field existed.

                  The id is only sent when the box is unchecked AND non-blank; the backend
                  re-gates on platform and on the id differing from the source. The source
                  account is still the one the campaign is READ from — only the create
                  moves. */}
              {isGoogleAds && (
                <FieldRow icon="account_balance" label="Advertiser">
                  <label className="cw-sameacct">
                    <input
                      type="checkbox"
                      checked={copyToSameAccount}
                      onChange={(e) => setCopyToSameAccount(e.target.checked)}
                    />
                    <span>Copying to same advertiser</span>
                  </label>
                  {!copyToSameAccount && (
                    <input
                      type="text"
                      inputMode="numeric"
                      className="need-in cw-sameacct-id"
                      placeholder="Advertiser ID — the account to create the copy in"
                      value={targetCustomerId || ''}
                      onChange={(e) => setTargetCustomerId(e.target.value)}
                      aria-label="Advertiser ID"
                    />
                  )}
                  <span className="fieldnote">
                    {copyToSameAccount
                      ? 'The copy is created in the same advertiser as the source.'
                      : 'Must be under the same manager account. Conversion goals and '
                        + "audiences are account-scoped and won't carry across."}
                  </span>
                </FieldRow>
              )}
              {/* GOOGLE ADS (Search + PMax) geo override. Suggests from Google's own
                  location list (any country) as the user types; a picked location is sent
                  as its geo target and launches exactly; a name added as typed is matched
                  when the copy is built and the review step shows the match. Blank keeps
                  the source geo. */}
              {isGoogleAds && (
                <FieldRow icon="place" label="Geo">
                  <div className="cw-geo-override">
                    <CopyGeoSuggest
                      value={searchGeo}
                      onChange={setSearchGeo}
                      suggest={geoChannelId && accountId && userId ? suggestGeo : null}
                    />
                  </div>
                  <span className="fieldnote">Replaces the source&apos;s locations. Leave blank to keep them.</span>
                </FieldRow>
              )}
              {/* GOOGLE ADS (Search + PMax) TARGET CPA. A stated amount pins the copy to
                  Maximize Conversions with that target — the only Search/PMax strategy
                  either creator applies a target CPA under, so the strategy has to move
                  with it. For PMax, blank means plain Maximize Conversions without the
                  source campaign's numeric CPA target. Search keeps its existing
                  blank-means-keep-source behavior. */}
              {isGoogleAds && (
                <FieldRow icon="track_changes" label="Target CPA">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="need-in"
                    placeholder={platform === 'google_pmax'
                      ? 'amount — blank removes a source CPA target'
                      : "amount — leave blank to keep the source's"}
                    value={gadsOverride('targetCpa')}
                    onChange={(e) => setGadsOverride('targetCpa', e.target.value)}
                  />
                  <span className="fieldnote">
                    {platform === 'google_pmax'
                      ? 'Sets Maximize Conversions with this target. If the source has a target CPA, leave blank to use Maximize Conversions without that target.'
                      : <>Sets the copy to Maximize Conversions with this target CPA. Leave blank to keep the source campaign&apos;s bidding.</>}
                  </span>
                </FieldRow>
              )}
              {/* URL TRACKING MOVED TO STEP 2. The campaign's tracking template and
                  final URL suffix, and every sitelink's own pair, are edited in the
                  URL panel on the review step. They were here first, but step 1 runs
                  BEFORE the build: it cannot list the sitelinks, and it cannot show
                  what any of these values produce — which is how a template that
                  collided with its own sitelink URLs shipped twice. Step 2 puts the
                  fields and the assembled URL on screen together.

                  Target CPA stays: it is bidding, not tracking, and it has no
                  per-entity dimension that needs the built plan to express. */}
              {SHOW_COPY_INTERPRET_UI && (
              <div className="opfoot">
                <span><b>{appliedCount} of {toggleCount}</b> operations will run</span>
                {declinedBlocking > 0 && (
                  <span style={{ color: '#7f1d1d' }}>· {declinedBlocking} declined change{declinedBlocking === 1 ? '' : 's'} will block the push</span>
                )}
                <div className="gap" />
                <span style={{ fontSize: 11 }}>Unchecking an operation is how you recover from a misread — the fields above stay as they are.</span>
              </div>
              )}
            </div>
          </div>

          {SHOW_COPY_INTERPRET_UI && interpretation?.warnings?.length > 0 && (
            <div className="gate-banner">
              <span className="material-icons">gpp_maybe</span>
              <div className="t"><b>Heads up.</b> {interpretation.warnings[0]}</div>
            </div>
          )}

          {nameError && !localError && (
            <div className="gate-banner">
              <span className="material-icons">error</span>
              <div className="t">{nameError}</div>
            </div>
          )}

          {localError && (
            <div className="gate-banner">
              <span className="material-icons">error</span>
              <div className="t">{localError}</div>
            </div>
          )}

          <div className="foot">
            {SHOW_COPY_INTERPRET_UI && (
              <span className="manual" onClick={() => setManualOpen((v) => !v)}>Set the parameters manually instead</span>
            )}
            <div className="gap" />
            <button className="cw-btn cw-btn-secondary" onClick={onBack}>Back</button>
            {/* Continue fires the real planning-mode builds and lands on the
                Adjust grid — the old "Confirm what's copied" picker step is
                gone (exclusion now happens as row deletions in that grid,
                against the real built template). */}
            <button className="cw-btn cw-btn-mint" onClick={handleContinue} disabled={nameChecking}>
              {nameChecking ? 'Checking the name…' : 'Build the copy plan'}
              {' '}<span className="material-icons">east</span>
            </button>
          </div>

          {/* The manual card exists ONLY as the "…set the parameters manually
              instead" escape hatch from the interpret UI, and it restates the
              very fields the card above already asks for (flight, budget) —
              so with SHOW_COPY_INTERPRET_UI off it is a pure duplicate and is
              hidden with the rest of that surface. Nothing is lost: start
              date, end date, budget and budget type all live in the FieldRows
              above, bound to the SAME formFields state. */}
          {SHOW_COPY_INTERPRET_UI && (
          <div className={`cw-card cw-card-describe cw-manualform${manualOpen ? ' show' : ''}`}>
            <div className="cw-card-h"><span className="ttl">Manual parameters</span><span className="sub">the same fields, asked as questions — no agent involved</span></div>
            <div className="cw-card-b">
              <div className="frow">
                <span className="flab">What&apos;s changing?</span>
                <div className="fval">
                  {INTENT_OPTIONS.map((o) => (
                    <span key={o.value} className={`pill${o.value === intent ? ' inferred' : ''}`} onClick={() => setIntent(o.value)}>{o.label}</span>
                  ))}
                </div>
              </div>
              <div className="frow">
                <span className="flab">Source</span>
                <div className="fval">
                  <span className="pill locked">{sourceCampaigns.length} campaign{sourceCampaigns.length === 1 ? '' : 's'} chosen on the previous page</span>
                </div>
              </div>
              <div className="frow">
                <span className="flab">Flight</span>
                <div className="fval">
                  <input type="date" className="need-in" value={formFields.startDate} onChange={(e) => setFormFields((prev) => ({ ...prev, startDate: e.target.value }))} />
                  <span className="material-icons arrow">east</span>
                  <input type="date" className="need-in" value={formFields.endDate} min={formFields.startDate || undefined} onChange={(e) => handleEndDateChange(e.target.value)} />
                </div>
              </div>
              <div className="frow">
                <span className="flab">{budgetInImpressions ? 'Budget (impressions)' : 'Budget'}</span>
                <div className="fval">
                  <input type="number" min="0" step={budgetInImpressions ? '1' : '0.01'} className="need-in" placeholder={budgetUnitWord} value={formFields.budget} onChange={(e) => setFormFields((prev) => ({ ...prev, budget: e.target.value }))} />
                  <select className="inline-sel" value={formFields.budgetType} onChange={(e) => setFormFields((prev) => ({ ...prev, budgetType: e.target.value }))}>
                    <option value="daily">Daily</option>
                    {!dailyOnly && <option value="total">Total</option>}
                  </select>
                </div>
              </div>
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
  );
}
